import { backofficeFetch } from '@/services/backoffice-api';
import type {
  BackofficeUserCommunicationSummary,
  BackofficeUserCommunicationsResponse,
  BackofficeUserDetailResponse,
  BackofficeUsersResponse,
} from './types';

export async function fetchBackofficeUsers(
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeUsersResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/users?${searchParams.toString()}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load users (${response.status})`);
  }

  return (await response.json()) as BackofficeUsersResponse;
}

export async function fetchBackofficeUserDetail(
  userId: string,
  signal?: AbortSignal
): Promise<BackofficeUserDetailResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(`/users/${encodeURIComponent(userId)}`, init);

  if (!response.ok) {
    throw new Error(`Failed to load user (${response.status})`);
  }

  return (await response.json()) as BackofficeUserDetailResponse;
}

export async function fetchBackofficeUserCommunicationSummary(
  userId: string,
  signal?: AbortSignal
): Promise<BackofficeUserCommunicationSummary> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/users/${encodeURIComponent(userId)}/communications/summary`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load communication summary (${response.status})`);
  }

  return (await response.json()) as BackofficeUserCommunicationSummary;
}

export async function fetchBackofficeUserEmailCommunications(
  userId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeUserCommunicationsResponse> {
  return fetchBackofficeUserCommunications(userId, 'email', searchParams, signal);
}

export async function fetchBackofficeUserInAppCommunications(
  userId: string,
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeUserCommunicationsResponse> {
  return fetchBackofficeUserCommunications(userId, 'in-app', searchParams, signal);
}

async function fetchBackofficeUserCommunications(
  userId: string,
  channel: 'email' | 'in-app',
  searchParams: URLSearchParams,
  signal?: AbortSignal
): Promise<BackofficeUserCommunicationsResponse> {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  };

  if (signal) {
    init.signal = signal;
  }

  const response = await backofficeFetch(
    `/users/${encodeURIComponent(userId)}/communications/${channel}?${searchParams.toString()}`,
    init
  );

  if (!response.ok) {
    throw new Error(`Failed to load communications (${response.status})`);
  }

  return (await response.json()) as BackofficeUserCommunicationsResponse;
}
